/**
 * SafetyModalsContext - Centralized safety modal state management
 *
 * Manages priority-based modal display for safety features:
 * - ToS Modal: CRITICAL priority (blocks all other modals until accepted)
 * - Crisis Resources Modal: HIGH priority (auto-triggers on crisis detection)
 * - Concern Flag Modal: MEDIUM priority ("I'm Concerned" user reporting)
 *
 * Design Principles:
 * - Single active modal at a time (prevents UI confusion)
 * - Priority queue for pending modals (higher priority interrupts lower)
 * - Persistent ToS acceptance state (AsyncStorage + Supabase)
 * - PostHog analytics integration for safety metrics
 *
 * Created: 2025-12-18
 * Phase: Moderation & Safety Infrastructure (Week 2)
 * Refactored: 2025-02-19 (NocoDB #739) - Split 687 lines into modular components
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from './AuthContext';
import { addBreadcrumb } from '../lib/analytics';

// Import separated modules
import {
  SafetyModalsContextType,
  SafetyModalsState,
  ModalType,
  TOS_STORAGE_KEY,
  CURRENT_TOS_VERSION,
  normalizeVersion,
  CrisisPractitionerData,
  DetectionFeedbackData,
  ConcernFlagData,
} from './safety-modals/types-constants';
import { safetyModalsReducer, initialState } from './safety-modals/reducer';
import { useTosModal } from './safety-modals/useTosModal';
import { useCrisisModal } from './safety-modals/useCrisisModal';
import { useCrisisPractitionerModal } from './safety-modals/useCrisisPractitionerModal';
import { useConcernFlagModal } from './safety-modals/useConcernFlagModal';
import { useDetectionFeedbackModal } from './safety-modals/useDetectionFeedbackModal';

// ============================================================================
// CONTEXT
// ============================================================================

const SafetyModalsContext = createContext<SafetyModalsContextType | undefined>(
  undefined
);

// ============================================================================
// PROVIDER
// ============================================================================

export function SafetyModalsProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(safetyModalsReducer, initialState);
  const { user } = useAuth();

  // P0 FIX: Prevent duplicate PROCESS_QUEUE dispatches during rapid open/close
  // Bug: queueMicrotask alone doesn't prevent double-dispatch when multiple
  // state updates fire the same useEffect in quick succession
  const isProcessingQueueRef = useRef(false);

  // Mirror state.modals in a ref so openModal can read current priority
  // without depending on the modals object (which changes on every update),
  // preserving the useCallback memoization for all consumers.
  const modalsRef = useRef(state.modals);
  useEffect(() => {
    modalsRef.current = state.modals;
  }, [state.modals]);

  // Initialize ToS acceptance state from storage
  useEffect(() => {
    let isMounted = true;

    const loadTosAcceptance = async () => {
      try {
        const stored = await AsyncStorage.getItem(TOS_STORAGE_KEY);
        if (stored && isMounted) {
          const { accepted, timestamp, version } = JSON.parse(stored);
          // MEDIUM FIX: Use normalized version comparison
          const isCurrentVersion = normalizeVersion(version) === normalizeVersion(CURRENT_TOS_VERSION);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              tosAccepted: accepted && isCurrentVersion,
              tosAcceptedAt: timestamp,
              tosVersion: version,
            },
          });
        } else if (isMounted) {
          dispatch({
            type: 'INITIALIZE',
            payload: { tosAccepted: false },
          });
        }
      } catch (error) {
        console.error('[SafetyModals] Failed to load ToS acceptance:', error);
        if (isMounted) {
          dispatch({
            type: 'INITIALIZE',
            payload: { tosAccepted: false },
          });
        }
      }
    };

    loadTosAcceptance();

    return () => {
      isMounted = false;
    };
  }, []);

  // Process queue when active modal closes
  // P0 FIX (2025-12-20): Combined queueMicrotask + ref guard for race condition prevention
  // Bug: queueMicrotask alone doesn't prevent double-dispatch when multiple
  // state updates fire the same useEffect in quick succession
  // Solution: isProcessingQueueRef acts as a lock to ensure single dispatch per cycle
  useEffect(() => {
    if (
      !state.activeModal &&
      state.priorityQueue.length > 0 &&
      state.isInitialized &&
      !isProcessingQueueRef.current
    ) {
      // Set lock before microtask to prevent concurrent entries
      isProcessingQueueRef.current = true;

      // Defer to next microtask to ensure state is synchronized
      queueMicrotask(() => {
        dispatch({ type: 'PROCESS_QUEUE' });
        // Release lock after dispatch completes
        isProcessingQueueRef.current = false;
      });
    }
  }, [state.activeModal, state.priorityQueue, state.isInitialized]);

  // Core modal actions
  const openModal = useCallback(
    (type: ModalType, data?: Record<string, unknown>) => {
      // Sentry breadcrumb: modal open for crash/session context (#761)
      addBreadcrumb('ui', 'Modal opened', {
        modal: type,
        priority: modalsRef.current[type]?.priority,
      });
      dispatch({ type: 'OPEN_MODAL', payload: { modalType: type, data } });
    },
    []
  );

  const closeModal = useCallback((type: ModalType) => {
    // Sentry breadcrumb: modal close for crash/session context (#761)
    addBreadcrumb('ui', 'Modal closed', { modal: type });
    dispatch({ type: 'CLOSE_MODAL', payload: { modalType: type } });
  }, []);

  const closeActiveModal = useCallback(() => {
    if (state.activeModal) {
      closeModal(state.activeModal);
    }
  }, [state.activeModal, closeModal]);

  // Use separated hooks for each modal type
  const { acceptTos, declineTos, checkTosAcceptance } = useTosModal(
    dispatch,
    state.tosVersion,
    user
  );

  const { triggerCrisisResources, dismissCrisis } = useCrisisModal(dispatch);

  const { triggerPractitionerPrompt } = useCrisisPractitionerModal(openModal);

  const { triggerConcernFlag } = useConcernFlagModal(openModal);

  const { triggerDetectionFeedback } = useDetectionFeedbackModal(openModal);

  const contextValue: SafetyModalsContextType = {
    state,
    openModal,
    closeModal,
    closeActiveModal,
    acceptTos,
    declineTos,
    checkTosAcceptance,
    triggerCrisisResources,
    dismissCrisis,
    triggerPractitionerPrompt,
    triggerConcernFlag,
    triggerDetectionFeedback,
  };

  return (
    <SafetyModalsContext.Provider value={contextValue}>
      {children}
    </SafetyModalsContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useSafetyModals() {
  const context = useContext(SafetyModalsContext);
  if (context === undefined) {
    throw new Error('useSafetyModals must be used within a SafetyModalsProvider');
  }
  return context;
}

export { CURRENT_TOS_VERSION, ModalType };
export type { CrisisPractitionerData, DetectionFeedbackData, ConcernFlagData };
